import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { services } from '../../services/api';
import { getUserTimezone, setUserTimezone as setStoredTimezone } from '../../utils/dateUtils';

export interface UserPreferences {
  timezone: string;
  // Notification preferences (from backend)
  email_enabled: boolean;
  email_on_down: boolean;
  email_on_recovery: boolean;
  email_on_degraded: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
}

interface UserPreferencesState {
  preferences: UserPreferences;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastSaved: string | null;
}

const initialState: UserPreferencesState = {
  preferences: {
    timezone: getUserTimezone(),
    email_enabled: true,
    email_on_down: true,
    email_on_recovery: true,
    email_on_degraded: false,
    quiet_hours_enabled: false,
    quiet_hours_start: null,
    quiet_hours_end: null,
  },
  isLoading: false,
  isSaving: false,
  error: null,
  lastSaved: null,
};

// Fetch user preferences from backend
export const fetchUserPreferences = createAsyncThunk(
  'userPreferences/fetch',
  async () => {
    const response = await services.auth.getNotificationPreferences();
    return response.data;
  }
);

// Save user preferences to backend
export const saveUserPreferences = createAsyncThunk(
  'userPreferences/save',
  async (preferences: Partial<UserPreferences>) => {
    const response = await services.auth.updateNotificationPreferences(preferences);
    return response.data;
  }
);

const userPreferencesSlice = createSlice({
  name: 'userPreferences',
  initialState,
  reducers: {
    setTimezone: (state, action: PayloadAction<string>) => {
      state.preferences.timezone = action.payload;
      // Also save to localStorage for immediate use
      setStoredTimezone(action.payload);
    },
    setPreference: (state, action: PayloadAction<{ key: keyof UserPreferences; value: any }>) => {
      const { key, value } = action.payload;
      (state.preferences as any)[key] = value;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        // Merge backend preferences with local timezone
        const backendPrefs = action.payload;
        state.preferences = {
          ...state.preferences,
          ...backendPrefs,
          // Use backend timezone if set, otherwise keep local
          timezone: backendPrefs.timezone || state.preferences.timezone,
        };
        // Sync timezone to localStorage
        if (backendPrefs.timezone) {
          setStoredTimezone(backendPrefs.timezone);
        }
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load preferences';
      })
      .addCase(saveUserPreferences.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveUserPreferences.fulfilled, (state, action) => {
        state.isSaving = false;
        state.lastSaved = new Date().toISOString();
        // Update with saved preferences
        const savedPrefs = action.payload;
        state.preferences = {
          ...state.preferences,
          ...savedPrefs,
        };
        // Sync timezone to localStorage
        if (savedPrefs.timezone) {
          setStoredTimezone(savedPrefs.timezone);
        }
      })
      .addCase(saveUserPreferences.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.error.message || 'Failed to save preferences';
      });
  },
});

export const { setTimezone, setPreference, clearError } = userPreferencesSlice.actions;
export default userPreferencesSlice.reducer;
